/**
 * ╔═══════════════════════════════════════════════════════════════╗
 * ║                                                               ║
 * ║   MetroJS - Configuration Constants                          ║
 * ║   Copyright (c) 2024 Metro Digital Solutions                  ║
 * ║                                                               ║
 * ║   MetroJSの設定定数を定義するファイル                         ║
 * ║                                                               ║
 * ╚═══════════════════════════════════════════════════════════════╝
 *
 * このファイルはMetroJSの設定定数を定義します。
 * アプリケーション全体で使用される共通の定数を管理します。
 *
 * @file consts.js
 * @module config/consts
 */

import config from "./config.js";

/**
 * DirectOrderの基本情報と定数定義
 * @type {Object}
 */
const Consts = {
  /**
   * プロジェクト名
   * @type {string}
   */
  NAME: "DirectOrder",
  APP_NAME: "DirectOrder",
  /**
   * プロジェクトの説明
   * @type {string}
   */
  DESCRIPTION: "ダイレクトオーダーシステム",

  /**
   * 著作権情報
   * @type {string}
   */
  COPYRIGHT: "Copyright (c) 2025 DirectOrder Digital Solutions",

  /**
   * プロジェクトのバージョン
   * @type {string}
   */
  VERSION: "1.0.0",

  /**
   * プロジェクトのURL
   * @type {string}
   */
  URL: "https://direct-order.jp",

  /**
   * プロジェクトのリポジトリURL
   * @type {string}
   */
  REPOSITORY: "https://github.com/direct-order/directorder",

  /**
   * MoviUSA:企業ID
   * @type {string}
   */
  MOVIUSA_CORP_ID: "14000001-000",
  MOVIUSA_CORP_ID_SEIBI: "24000001",

  /**
   * セッションキー：車検証検索内容（車検証検索時、バックオーダー登録）
   * @type {string}
   */
  SESSION_SHAKENSYO: "S_SHAKENSYO",

  BASE_STYLE: {
    SELECT: {
      width: "150px",
      padding: "3px 5px",
      border: "1px solid #ccc",
      borderRadius: "3px",
    },
  },

  /**
   * 分類：親
   * @type {number}
   */
  BUNRUI_OYA: 0,

  /**
   * デフォルトステータス：公開中
   * @type {number}
   */
  DEFAULT_STATUS: 5,

  /**
   * 分類コード：用品
   * @type {Array}
   */
  BUNRUICD_SUPPLIES: ["20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30"],
  BUNRUICD_SUPPLIES_IN: "'20','21','22','23','24','25','26','27','28','29','30'",

  /**
   * ロゴファイルのデフォルト
   * @type {string}
   */
  LOGO_FILE_NAME: "logo.png",

  /**
   * 規約ファイルのデフォルト
   * @type {string}
   */
  AGREEMENT_AB_FILE_NAME: "agreement.txt", // ab用
  AGREEMENT_YZ_FILE_NAME: "agreement_yz.txt", // yz用

  /**
   * 規約DLファイルのデフォルト
   * @type {string}
   */
  AGREEMENT_AB_DL_FILE_NAME: "ダイレクトオーダー_規約.pdf",
  AGREEMENT_YZ_DL_FILE_NAME: "ダイレクトオーダー_規約.pdf",

  /**
   * マニュアルDLファイルのデフォルト
   * @type {string}
   */
  MANUAL_AB_DL_FILE_NAME: "DirectOrderマニュアル_整備鈑金事業者様用.pdf", // ab用
  MANUAL_YZ_DL_FILE_NAME: "DirectOrderマニュアル_部品販売事業者様用.pdf", // yz用

  /**
   * 更新情報ファイルのデフォルト
   * @type {string}
   */
  UPDATE_AB_DL_FILE_NAME: "index.html", // ab用
  UPDATE_YZ_DL_FILE_NAME: "index.html", // yz用

  /**
   * 解体部品の手数料
   * @type {number}
   */
  RECYCLE_PARTS_FEE: 0.05,

  /**
   * 解体車輌の手数料
   * @type {number}
   */
  RECYCLE_CAR_FEE: 0.1,

  /**
   * 損保用上乗せ％
   * @type {number}
   */
  ISRC_PARTS_FEE: 0.2,

  /**
   * 代理登録の上乗せ％ 180711 tai
   * 0.05→0.1へ変更 210802 tai
   * @type {number}
   */
  DAIRI_TELFAX_FEE: 0.1,

  /**
   * (上乗せグループ以外全)上乗せ％ 191015 tai
   * @type {number}
   */
  ALL_ADD_FEE: 0.1,

  /**
   * 送料税込へ切り替わる日付
   * @type {string}
   */
  CARRY_TAXIN_DATE: "2016/01/01",

  /**
   * リパーツ(見積送信する企業ID)
   * @type {Array}
   */
  RE_PARTS_CORP: [
    "14001300-000", // リ・パーツ東京
    "14001301-000", // リ・パーツ関西
    "14001302-000", // リ・パーツ九州
  ],

  /**
   * カーソリューションズ対応
   * (発送先を編集できるなど)
   * @type {Array}
   */
  HASSOSAKI_EDIT_CORP: [
    "24001522-000", // イチネン
    "24001522-001", // イチネン 聲元様専用 190610 tai
    // '24000397-000',  // 日本カーソリューションズ
  ],

  /**
   * イチネン対応
   * (発送先を別窓から選択できるなど)
   * @type {Array}
   */
  HASSOSAKI_SELECT_CORP: [
    "24001522-000", // イチネン
    "24001522-001", // イチネン 聲元様専用 190610 tai
    "24000001-001", // movivi test用
  ],

  /**
   * ロジコ対応 180830 tai
   * (請求書ボタンを非表示するグループ)
   * @type {Array}
   */
  SEIKYUSHO_BUTTON_GROUP: [
    52, // 京都商工
    74, // ロジコ
    90, // イチネンパーツ 190702 tai
    43, // JU長野パーツサービス 200710 tai
  ],

  /**
   * ユーキ塗料 (ASパーツ分) add by highspeed 181010
   * 数が多くなったので、arrayで対応する 18/12/19
   * @type {Array}
   */
  YUKITORYO_GROUP_ID: [
    "55", // ユーキ塗料 dev
    "73", // ユーキ
    "76", // イズミ
    "77", // 川北
    "78", // 高倉塗料
    "79", // サンヴェール
    "80", // 第一塗料
    "82", // マルヤ塗料
    "84", // 近藤
    "87", // 岡久
  ],

  /**
   * ランディングページURL
   * @type {Object}
   */
  DO_LANDING_URL: {
    "https://direct-order.jp/": "https://direct-order.jp/",
    "https://www.direct-order.jp/": "https://www.direct-order.jp/",
    "https://truck-ichi.direct-order.jp/": "https://truck-ichi.direct-order.jp/", // トラック市
    "https://auction-daio.direct-order.jp/": "https://auction-daio.direct-order.jp/", // オークション大王
    "https://vehiclenext.direct-order.jp/": "https://vehiclenext.direct-order.jp/", // ビークルネクスト
  },

  /**
   * 連携用
   * NWS
   * @type {string}
   */
  NWS_SEND_MAIL: "directorder@cardoctor.sakura.ne.jp",
  NWS_GROUP_ID: 24,

  /**
   * API用
   * ログ出力先
   * @type {string}
   */
  LOG_OUTPUT_FILE: "/var/www/html/ab/htdocs/app/tmp/",

  /**
   * DirectOrder整備URL
   * 環境変数 DO_AB_URL から取得（デフォルト: "https://ab.direct-order.jp/"）
   * @type {string}
   */
  DO_AB_URL: config.DO_AB_URL,

  /**
   * LOGICO-API-ERROR-URL
   * @type {string}
   */
  LOGICO_AB_ERROR_URL: "https://ab.direct-order.jp/errorcpb.html",

  /**
   * DirectOrder生産者URL
   * 環境変数 DO_YZ_URL から取得（デフォルト: "https://yz.direct-order.jp/"）
   * @type {string}
   */
  DO_YZ_URL: config.DO_YZ_URL,

  /**
   * DirectOrder損保URL
   * @type {string}
   */
  DO_ISRC_URL: "https://isrc.direct-order.jp/",

  /**
   * システムメールアドレス設定
   * @type {Object}
   */
  SYSTEM_MAIL: {
    /** システムメールアドレス（部品） */
    SYSTEM_MAIL: "system-info@direct-order-sys.jp",
    /** TEL/FAX用メールアドレス */
    TEL_FAX_MAIL: "support@direct-order-sys.jp",
    /** システム宛メールアドレス */
    DO_SYS_MAIL: "system-info@direct-order-sys.jp",
    /** 価格変更等の運用通知先（PHP dosys@direct-order.jp 相当） */
    DOSYS_MAIL: "dosys@direct-order.jp",
  },

  /**
   * 事務局メールアドレス（pr_mail 請求書・計算書送信の差出・問い合わせ先）
   * @type {string}
   */
  //JIMU_MAIL: "jimukyoku@direct-order.jp",
  JIMU_MAIL: "support@direct-order-sys.jp",

  /**
   * 請求書・計算書メール送信（PrMail）用ログ設定
   * 環境変数 PR_MAIL_LOG_DIR / RT_AB_CORP_PATH / RT_YZ_CORP_PATH で上書き可
   * @type {Object}
   */
  PR_MAIL_LOG: {
    /** 処理ログディレクトリ（pr_mail_YYYYMM.log の出力先） */
    LOG_DIR: process.env.PR_MAIL_LOG_DIR || "",
    /** 送信OK企業ログ（請求書）rt_ab_corp.log のパス */
    RT_AB_CORP_PATH: process.env.RT_AB_CORP_PATH || "",
    /** 送信OK企業ログ（計算書）rt_yz_corp.log のパス */
    RT_YZ_CORP_PATH: process.env.RT_YZ_CORP_PATH || "",
  },

  /**
   * 京都整備のグループID 180830 tai
   * (上乗せグループが増えたため、京都独自対応用に設置)
   * @type {number}
   */
  KYOTO_GROUP_ID: 52,

  /**
   * トラック市のグループID
   * @type {number}
   */
  TRUCK_ICHI_GROUP_ID: 16,

  /**
   * ロジコのグループID
   * @type {number}
   */
  LOGICO_GROUP_ID: 74,

  /**
   * FinalPartsのグループID 190610 tai
   * @type {number}
   */
  FINAL_PARTS_GROUP_ID: 88,

  /**
   * イチネンパーツのグループID 190702 tai
   * @type {number}
   */
  ICHINEN_GROUP_ID: 90,

  /**
   * JU長野パーツサービスのグループID 200710 tai
   * @type {number}
   */
  JUNPS_GROUP_ID: 43,

  /**
   * グローバルクレストのグループID 20260518 masami
   * @type {number}
   */
  GLOBAL_CREST_GROUP_ID: 123,

  /**
   * タックス本部のグループID 20260719 masami
   * @type {number}
   */
  TAX_HONBU_GROUP_ID: 124,

  /**
   * DHM_Groups.ShimeKubun（締め処理区分）
   * @type {Object}
   */
  SHIME_KUBUN: {
    /** 通常締め処理 */
    TSUJO: 0,
    /** 商工組合締め処理 */
    SHOKO_KUMIAI: 1,
  },

  /**
   * 商工組合請求・報告書メール送信先（PrPsCController::report_output 移植用）
   * @type {Object}
   */
  REPORT_OUTPUT_MAIL: {
    /** 京都整備（groupID 52）送信先 */
    KYOTO_TO: ["info@kasnet.or.jp"],
    // KYOTO_TO: ["support@direct-order-sys.jp"],
    /** 京都整備 BCC */
    //KYOTO_BCC: ["jimukyoku@direct-order.jp", "dosys@direct-order.jp"],
    KYOTO_BCC: ["support@direct-order-sys.jp"],
    /** JU長野（groupID 43）送信先 */
    //JUNPS_TO: ["jimukyoku@direct-order.jp"],
    JUNPS_TO: ["support@direct-order-sys.jp"],
    /** JU長野 BCC */
    //JUNPS_BCC: ["dosys@direct-order.jp"],
    JUNPS_BCC: ["support@direct-order-sys.jp"],
    /** 送信元（京都整備は FROM_MAIL、JU長野は junps-info@direct-order.co.jp） */
    //JUNPS_FROM: "junps-info@direct-order.co.jp",
    JUNPS_FROM: "support@direct-order-sys.jp",
    /** グローバルクレスト（groupID 123）送信先 */
    GLOBAL_CREST_TO: ["support@direct-order-sys.jp"],
    /** グローバルクレスト BCC */
    GLOBAL_CREST_BCC: ["support@direct-order-sys.jp"],
    /** タックス本部（groupID 124）送信先 */
    TAX_HONBU_TO: ["support@direct-order-sys.jp"],
    /** タックス本部 BCC */
    TAX_HONBU_BCC: ["support@direct-order-sys.jp"],
  },

  /**
   * 時間帯定数（Ans_Limit算出用）
   * @type {Object}
   */
  ANS_LIMIT_TIME: {
    /** 開始時刻（時） */
    START_HOUR: 9,
    /** 終了時刻（時） */
    END_HOUR: 14,
    /** デフォルト時刻（時） */
    DEFAULT_HOUR: 12,
    /** 加算時間（時間） */
    ADD_HOURS: 3,
  },

  /**
   * 日付フォーマット定数
   * @type {Object}
   */
  DATE_FORMAT: {
    /** YYYY/MM/DD形式 */
    SLASH_DATE: "YYYY/MM/DD",
    /** YYYY-MM-DD形式 */
    HYPHEN_DATE: "YYYY-MM-DD",
    /** YYYY-MM-DD HH:mm:ss形式 */
    DATETIME: "YYYY-MM-DD HH:mm:ss",
    /** M月D日 H時m分まで有効形式 */
    ANS_LIMIT_DISPLAY: "M月D日 H時m分まで有効",
  },

  /**
   * プラスのグループID 201116 tai
   * @type {number}
   */
  PLUS_GROUP_ID: 115,

  /**
   * お試し会員用のグループID
   * @type {number}
   */
  TEST_GROUP_ID: 46, // 本番
  // TEST_GROUP_ID: 20,  // dev

  /**
   * メールタイトル設定
   * @type {Object}
   */
  MAIL_SUBJECT: {
    /** 見積依頼を行いました */
    MITSUMORI_IRAI: "見積依頼を行いました",
    /** 見積依頼のご案内 */
    MITSUMORI_IRAI_ANNAI: "見積依頼のご案内",
    /** 注文を行いました */
    TYUMON_KAKU: "注文を行いました",
    /** 注文がありました */
    TYUMON_KAKU_ANNAI: "注文がありました",
    /** 評価を行いました */
    EVAL_KANRYO: "評価を行いました",
    /** 見積を行いました */
    ORDR_MITSUMORI_KAITO1: "見積を行いました",
    /** 見積回答がありました */
    ORDR_MITSUMORI_KAITO2: "見積回答がありました",
    /** 受注を確定いたしました */
    ORDR_ZAIKO_KAITO1_1: "受注を確定いたしました",
    /** 注文が確定いたしました */
    ORDR_ZAIKO_KAITO1_2: "注文が確定いたしました",
    /** 受注をキャンセルいたしました */
    ORDR_ZAIKO_KAITO2_1: "受注をキャンセルいたしました",
    /** 部品在庫無しのおしらせ */
    ORDR_ZAIKO_KAITO2_2: "部品在庫無しのおしらせ",
    /** 発送情報登録を受け付けました */
    ORDR_HASSO1: "発送情報登録を受け付けました",
    /** 商品発送のお知らせ */
    ORDR_HASSO2: "商品発送のお知らせ",
    /** 返品処理を行いました */
    ORDR_HENPIN1: "返品処理を行いました",
    /** 返品受付のお知らせ */
    ORDR_HENPIN2: "返品受付のお知らせ",
    /** 値引処理を行いました */
    ORDR_NEBIKI1: "値引処理を行いました",
    /** 値引のお知らせ */
    ORDR_NEBIKI2: "値引のお知らせ",
    /** 見積依頼を行いました（車輌） */
    CAR_MITSUMORI_IRAI1: "見積依頼を行いました",
    /** 見積依頼のご案内（車輌） */
    CAR_MITSUMORI_IRAI2: "見積依頼のご案内",
    /** 落札者を確定いたしました */
    CAR_BAIKYAKU1: "落札者を確定いたしました",
    /** 落札のご案内 */
    CAR_BAIKYAKU2_1: "落札のご案内",
    /** 流札のご案内 */
    CAR_BAIKYAKU2_2: "流札のご案内",
    /** 入札を行いました */
    CARORDR_COMPLETE1: "入札を行いました",
    /** 入札がありました */
    CARORDR_COMPLETE2: "入札がありました",
    /** ご質問を承りました */
    NEW_QUESTION1: "ご質問を承りました",
    /** ご依頼について質問がありました */
    NEW_QUESTION2: "ご依頼について質問がありました",
    /** 回答を受け付けました */
    NEW_ANSWER1: "回答を受け付けました",
    /** いただいたご質問に回答がありました */
    NEW_ANSWER2: "いただいたご質問に回答がありました",
    /** ご質問を承りました（ABQA） */
    ABQA_QUESTION1: "ご質問を承りました",
    /** お見積について質問がありました */
    ABQA_QUESTION2: "お見積について質問がありました",
    /** 回答を受け付けました（ABQA） */
    ABQA_ANSWER1: "回答を受け付けました",
    /** いただいたご質問に回答がありました（ABQA） */
    ABQA_ANSWER2: "いただいたご質問に回答がありました",
    /** 車輌もぎとり会場から見積依頼を行いました */
    MOGITORI_IRAI: "車輌もぎとり会場から見積依頼を行いました",
    /** 【部品取り車輌】公開中の車輌へのお問合せ【DirectOrder】 */
    MOGITORI_IRAI_ANNAI: "【部品取り車輌】公開中の車輌へのお問合せ【DirectOrder】",
  },

  /**
   * システム区分設定
   * @type {Object}
   */
  KUBUN: {
    /** 部品 */
    PARTS: "P",
    /** 車輌 */
    CARS: "C",
    /** 現車 */
    GENSHA: "G",
  },

  /**
   * 見積番号連番の桁数
   * @type {number}
   */
  MITSUMORI_KETA: 8,

  /**
   * 伝票番号連番の桁数
   * @type {number}
   */
  DENPYO_KETA: 7,

  /**
   * 見積依頼テーブル ステータス設定
   * @type {Object}
   */
  REQUEST_STATUS: {
    /** 見積入力中 */
    NYURYOKUCHU: 1,
    /** 見積依頼中 */
    IRAICHU: 5,
    /** 回答 */
    KAITO: 6,
    /** 受注 */
    JYUTYU: 7,
    /** 受注済み */
    JYUTYUZUMI: 8,
    /** 見積期限切れ */
    KIGENGIRE: 9,
    /** 注文確定 */
    KAKUTEI: 10,
    /** 完了 */
    KANRYO: 10,
    /** 無効 */
    MUKO: 99,
  },

  /**
   * 見積依頼テーブル 既読フラグ設定
   * @type {Object}
   */
  REQUEST_ALFLG: {
    /** 回答少ない */
    KAITO_MIN: -1,
    /** 未読 */
    MIDOKU: 0,
    /** 既読 */
    KIDOKU: 1,
  },

  /**
   * テーブルの有効・無効フラグ設定
   * @type {Object}
   */
  STATUS: {
    /** 仮登録 */
    KARI: 1,
    /** 有効 */
    YUKO: 5,
    /** 受注 */
    JYUTYU: 7,
    /** 無効（9） */
    MUKO_9: 9,
    /** 無効（99） */
    MUKO_99: 99,
  },

  /**
   * 会員種別マスタ（お知らせ管理・業種選択等）
   * @type {Object}
   */
  CorpKindMaster: {
    1: "リサイクル",
    2: "整備",
    3: "CS",
    4: "損保",
    9: "本部",
  },

  /**
   * 都道府県関連の定数
   * @type {Object}
   */
  AREA: {
    /** 都道府県ステータス：有効 */
    STATUS_YUKO: 5,
    /** 都道府県ステータス：無効 */
    STATUS_MUKO: 9,
    /** 都道府県の総数 */
    TOTAL_PREFECTURE_COUNT: 47,
    /** 部品・車輌フラグ：部品 */
    BUHSHA_FLG_PARTS: 1,
  },

  /**
   * イメージフォルダ設定
   * @type {Object}
   */
  IMAGE_FOLDER: {
    /** イメージファイル格納フォルダ（買う側） */
    IMAGE_FOLDER: "images/PhotoImage",
    /** S3のイメージファイル格納URL（環境変数 S3_IMAGE_FOLDER を参照） */
    IMAGE_FOLDER_S3: config?.amazon?.Metrojs?.S3_image_folder ?? "https://d1c17ackk8jb34.cloudfront.net",
    IMAGE_FOLDER_S3_DIRECT: config?.amazon?.Metrojs?.S3_image_folder_direct ?? "https://direct-order-dev-01.s3.amazonaws.com",
    /** S3のイメージファイル格納フォルダ（本番） */
    // IMAGE_FOLDER_S3: "https://d3k81pu8ix8try.cloudfront.net",
    IMAGE_FOLDER_PRO_S3: "https://d3k81pu8ix8try.cloudfront.net",
    /** サムネイル格納フォルダ（買う側） */
    IMAGE_SUB_FORDER: "thumbnail",
    /** イメージファイル格納フォルダ（売る側） */
    IMAGE_FOLDER_SELL: "images/PhotoImage",
    /** サムネイル格納フォルダ（売る側） */
    IMAGE_SUB_FORDER_SELL: "thumbnail",
  },
  /**
   * Q&Aステータス設定
   */
  /*
  QA_STATUS: {
    MIKAITO: 0, // 未回答
    KAITOZUMI: 1, // 回答済み
  },
  */

  QA_STATUS: {
    /** 未回答 */
    MIKAITO: 1,
    /** 回答済み */
    KAITOZUMI: 5,
    MUKO: 9,
  },

  /**
   * メール送信フラグ（PmmCorpaccounts.EmailSend_flg）
   * @type {Object}
   */
  EMAIL_SEND_FLG: {
    /** 送信不可 */
    SEND_NG: 0,
    /** 送信可 */
    SEND_OK: 1,
  },

  /**
   * 発注フラグ設定
   * @type {Object}
   */
  HACCHU_FLG: {
    /** 見積回答 */
    KAITO: 0,
    /** 仮注文（在庫未回答） */
    KARI: 1,
    /** 注文確定 */
    KAKUTEI: 2,
    /** 発送済 */
    HASOZUMI: 5,
    /** 回答 */
    KAITOU: 6,
    /** 値引 */
    NEBIKI: 8,
    /** 返品 */
    HENPIN: 9,
    /** 在庫なし回答済 */
    ZAIKONASHI: 13,
    /** 在庫回答時間切れ */
    KIGENGIRE: 14,
  },

  /**
   * 解体側写真登録モード設定
   * @type {Object}
   */
  WRECKER_PHOTO: {
    /** 仮登録書き込みモード */
    KARI_WRT: 1,
    /** 仮登録参照モード */
    KARI_REF: 2,
    /** 有効参照モード */
    YUKO_REF: 3,
  },

  /**
   * 解体側写真登録モード定数（PHP互換）
   * @type {Object}
   */
  WRECKER_PHOTO_YUKO_REF: 3,

  /**
   * 入力経過フラグ設定
   * @type {Object}
   */
  INPUT_STATUS: {
    /** 依頼情報入力画面 A-2-3（デフォルト） */
    DEFAULT: 0,
    /** 依頼情報入力画面 A-2-3 */
    IRAI: 3,
    /** 部位選択画面 A-2-5 */
    BUI: 4,
    /** エリア選択画面 A-2-6 */
    AREA: 5,
    /** 見積期限画面 A-2-7 */
    PERIOD: 6,
    /** 写真添付画面 A-2-8 */
    PHOTO: 7,
    /** 車輛状態１入力画面 B-2-4-1 */
    CAR_STATUS1: 103,
    /** 車輛状態２画面 B-2-4-2 */
    CAR_STATUS2: 104,
    /** エリア選択画面 B-2-5 */
    CAR_AREA: 105,
    /** 見積期限画面 B-2-6 */
    CAR_PERIOD: 106,
    /** 写真添付画面 B-2-7 */
    CAR_PHOTO: 107,
  },

  /**
   * 4CD設定
   * @type {Object}
   */
  FOUR_CD: {
    /** 4CD特定できない場合 */
    UNDEFINE: "0001",
    /** 4CD車輌場合 */
    CARS: "8060",
  },

  /**
   * 自社品/他社品設定
   * @type {Object}
   */
  GOODS: {
    /** 自社品 */
    INHOUSE: 1,
    /** 他社品 */
    OTHER: 2,
  },

  /**
   * 発送方法設定
   * @type {Object}
   */
  HAISO_KUBUN: {
    /** 発送 */
    HASSO: 1,
    /** 店頭渡し */
    WATASHI: 2,
    /** 自社配送 */
    JISYA: 3,
  },

  /**
   * ラジオボタン共通設定
   * @type {Object}
   */
  RADIO: {
    /** 有 */
    ARI: 1,
    /** 不明 */
    FUMEI: 5,
    /** 無 */
    NASHI: 9,
    /** 自家用 */
    JIKAYOU: 1,
    /** レンタカー */
    RENTCAR: 2,
    /** 修復歴：有 */
    SHUFUKU_ARI: 1,
    /** 修復歴：無 */
    SHUFUKU_NASHI: 9,
    /** 修復歴：不明 */
    SHUFUKU_FUMEI: 5,
    /** 修復歴：事故現状 */
    SHUFUKU_JIKO: 7,
    /** 走行距離：不明 */
    KYORI_FUMEI: -1,
  },

  /**
   * 損傷箇所設定
   * @type {Object}
   */
  SONSHOU: {
    /** 前部 */
    SONSHOU_01: 1,
    /** 後部 */
    SONSHOU_02: 2,
    /** 右側 */
    SONSHOU_03: 3,
    /** 左側 */
    SONSHOU_04: 4,
    /** ルーフ */
    SONSHOU_05: 5,
    /** 盗難 */
    SONSHOU_06: 6,
    /** 水没 */
    SONSHOU_07: 7,
    /** 炎上 */
    SONSHOU_08: 8,
    /** エアバッグ展開 */
    SONSHOU_09: 9,
    /** 横転 */
    SONSHOU_10: 10,
  },

  /**
   * 足回り設定
   * @type {Object}
   */
  ASHIMAWARI: {
    /** 不明 */
    FUMEI: 1,
    /** OK */
    OK: 5,
    /** NG */
    NG: 9,
    /** 前部 */
    ASHIMAWARI_01: 1,
    /** 後部 */
    ASHIMAWARI_02: 2,
    /** 右側 */
    ASHIMAWARI_03: 3,
    /** 左側 */
    ASHIMAWARI_04: 4,
  },

  /**
   * 契約種別設定
   * @type {Object}
   */
  CORP_KEIYAKU: {
    /** リサイクル */
    CORP_KEIYAKU_01: 1,
    /** 整備 */
    CORP_KEIYAKU_02: 2,
    /** CS */
    CORP_KEIYAKU_03: 3,
    /** 損保 */
    CORP_KEIYAKU_04: 4,
    /** テスト */
    CORP_KEIYAKU_09: 9,
  },

  /**
   * データベースカラム名の設定
   * プロジェクトごとに設定が必要なカラム名
   * @type {Object}
   */
  DB_COLUMNS: {
    /**
     * 登録者カラム名
     * プロジェクトによって異なる場合があります
     * 例: regist_user_id, registuser, created_by など
     * @type {string}
     */
    REGIST_USER: "registid",

    /**
     * 更新者カラム名
     * プロジェクトによって異なる場合があります
     * 例: update_user_id, updateuser, updated_by など
     * @type {string}
     */
    UPDATE_USER: "henkokind",

    /**
     * 登録日時カラム名
     * プロジェクトによって異なる場合があります
     * 例: created_at, regist_datetime, created_date など
     * @type {string}
     */
    REGIST_DATETIME: "created",

    /**
     * 更新日時カラム名
     * プロジェクトによって異なる場合があります
     * 例: updated_at, update_datetime, updated_date など
     * @type {string}
     */
    UPDATE_DATETIME: "modified",
  },

  /**
   * アプリケーションのルートパス設定
   * @type {Object}
   */
  ROUTES: {
    /**
     * ホームページのパス
     * @type {string}
     */
    HOME: "/agreement",
  },

  // 空の選択肢（デフォルト表示用）
  NOT_SELECT_OPTIONS: [{ key: "not_select", value: "", label: "▼選択してください" }],
  EMPTY_OPTIONS: [{ key: "empty", value: "-1", label: "" }],

  /**
   * 選択肢設定（htdocs_yz/app/config/selection.phpから移行）
   * @type {Object}
   */
  SELECTION: {
    RE_ESTIMATE_REASON: [
      { key: "1", value: "1", label: "注文したが、在庫が無かった" },
      { key: "2", value: "2", label: "価格が高かった" },
      { key: "3", value: "3", label: "色違い" },
      { key: "4", value: "4", label: "品質・状態面" },
      { key: "5", value: "5", label: "ユーザーによる修理キャンセル→再依頼" },
      { key: "7", value: "7", label: "前回見積り回答ゼロ" },
      { key: "8", value: "8", label: "追加部品の見積依頼" },
      { key: "99", value: "99", label: "その他" },
    ],
    /**
     * ステータス
     * @type {Array}
     */
    STATUS: [
      { key: 1, value: "入力途中" },
      { key: 5, value: "見積依頼中" },
      { key: 9, value: "見積期限切れ" },
      { key: 99, value: "無効" },
    ],

    /**
     * 検索条件状態
     * @type {Array}
     */
    SEARCH_STATUS: [
      { key: 0, value: "全て" },
      { key: 5, value: "見積依頼中" },
      { key: 7, value: "見積回答有" },
      { key: 9, value: "見積期限切れ" },
      { key: 20, value: "発送済" },
      { key: 21, value: "注文中" },
      { key: 22, value: "-" },
      { key: 90, value: "" },
    ],

    /**
     * ステータス（車輛整備側）
     * @type {Array}
     */
    CAR_STATUS: [
      { key: 5, value: "見積依頼中" },
      { key: 6, value: "見積回答有" },
      { key: 7, value: "売却確定" },
      { key: 10, value: "売却完了" },
      { key: 20, value: "売却未成立" },
    ],

    /**
     * シフト方式
     * @type {Array}
     */
    TRANS_MISSION: [
      { key: "AT", value: "AT" },
      { key: "MT", value: "MT" },
      { key: "CVT", value: "CVT" },
      { key: "その他", value: "その他" },
    ],

    /**
     * 駆動方式
     * @type {Array}
     */
    KUDOU_HOUSHIKI: [
      { key: "FF", value: "FF" },
      { key: "FR", value: "FR" },
      { key: "MR", value: "MR" },
      { key: "2WD", value: "2WD" },
      { key: "4WD", value: "4WD" },
      { key: "その他", value: "その他" },
    ],

    /**
     * ステータス（部品解体側）
     * @type {Array}
     */
    ORDERED_STATUS: [
      { key: 5, value: "未回答" },
      { key: 6, value: "回答済" },
      { key: 7, value: "受注確認" },
      { key: 8, value: "受注済" },
      { key: 9, value: "完了" },
      { key: 10, value: "完了" },
      { key: 94, value: "未受注(他成約)" },
      { key: 95, value: "未回答(終了分)" },
      { key: 96, value: "未受注" },
      { key: 97, value: "未受注" },
      { key: 98, value: "受注処理済" },
      { key: 99, value: "無効" },
    ],

    /**
     * ステータス（車輌解体側）
     * @type {Array}
     */
    CAR_ORDERED_STATUS: [
      { key: 5, value: "未回答" },
      { key: 6, value: "回答済" },
      { key: 7, value: "落札" },
      { key: 10, value: "引取済" },
      { key: 20, value: "未入札" },
      { key: 21, value: "流札" },
      { key: 99, value: "無効" },
    ],

    /**
     * 発送可能日
     * @type {Array}
     */
    DELIVERY_POSSIBLE: [
      { key: 1, value: "当日" },
      { key: 2, value: "翌日" },
      { key: 3, value: "翌々日" },
      { key: 5, value: "5営業日以内" },
    ],

    /**
     * リサイクル料金預託
     * @type {Array}
     */
    RECYCLE_STATUS: [
      { key: 1, value: "有" },
      { key: 9, value: "無" },
    ],

    /**
     * 書類有無
     * @type {Array}
     */
    SHORUI_STATUS: [
      { key: 1, value: "書類有" },
      { key: 9, value: "書類無" },
      { key: 5, value: "どちらでも" },
    ],

    /**
     * 販売区分
     * @type {Array}
     */
    HANBAI_KUBUN: [
      { key: 1, value: "中古車" },
      { key: 9, value: "解体車" },
      { key: 5, value: "どちらでも" },
    ],

    /**
     * 車歴
     * @type {Array}
     */
    SYAREKI_STATUS: [
      { key: 1, value: "自家用" },
      { key: 2, value: "レンタカー" },
    ],

    /**
     * 車歴 表示用
     * @type {Array}
     */
    SYAREKI_STATUS2: [
      { key: 1, value: "自家用" },
      { key: 2, value: "レンタカー" },
      { key: 3, value: "その他" },
      { key: 4, value: "事業用" },
    ],

    /**
     * 修復歴
     * @type {Array}
     */
    SHUFUKU_STATUS: [
      { key: 1, value: "有" },
      { key: 5, value: "不明" },
      { key: 9, value: "無" },
      { key: 7, value: "事故現状" },
    ],

    /**
     * 車輌看板
     * @type {Array}
     */
    SYARYOKANBAN_STATUS: [
      { key: 1, value: "有" },
      { key: 5, value: "不明" },
      { key: 9, value: "無" },
    ],

    /**
     * 足回り
     * @type {Array}
     */
    ASHIMAWARI_STATUS: [
      { key: 1, value: "不明" },
      { key: 5, value: "OK" },
      { key: 9, value: "NG" },
    ],

    /**
     * 書類可否
     * @type {Array}
     */
    SHORUI_FLG: [
      { key: 1, value: "要" },
      { key: 5, value: "どちらでも" },
      { key: 9, value: "不要" },
    ],

    /**
     * 発送可能日
     * @type {Array}
     */
    HASSO_STATUS: [
      { key: 1, value: "当日" },
      { key: 2, value: "翌日" },
      { key: 3, value: "翌々日" },
      { key: 5, value: "5営業日以内" },
      { key: 10, value: "10営業日以内" },
    ],

    /**
     * 発送方法
     * @type {Array}
     */
    HAISO_KUBUN: [
      { key: 1, value: "発送" },
      { key: 2, value: "店頭渡" },
      { key: 3, value: "自社配送" },
    ],

    /**
     * 燃料種別
     * @type {Array}
     */
    E_KUBUN: [
      { key: 1, value: "ガソリン" },
      { key: 2, value: "軽油" },
      { key: 3, value: "ＬＰＧ" },
      { key: 4, value: "電気" },
      { key: 5, value: "CNG" },
      { key: 99, value: "その他" },
    ],

    /**
     * 在庫
     * @type {Array}
     */
    ZAIKO: [
      { key: 0, value: "-" },
      { key: 1, value: "-" },
      { key: 2, value: "〇" },
      { key: 5, value: "〇" },
      { key: 8, value: "〇" },
      { key: 9, value: "" },
      { key: 13, value: "×" },
      { key: 14, value: "×" },
    ],

    /**
     * エンジン状態
     * @type {Array}
     */
    ENGINE_STATUS: [
      { key: "不明", value: "不明" },
      { key: "エンジン始動不可", value: "エンジン始動不可" },
      { key: "エンジン始動可", value: "エンジン始動可" },
      { key: "エンジン始動可・走行不可", value: "エンジン始動可・走行不可" },
      { key: "バッテリーつなげば始動可", value: "バッテリーつなげば始動可" },
    ],

    /**
     * 鍵状態
     * @type {Array}
     */
    KEY_STATUS: [
      { key: "不明", value: "不明" },
      { key: "あり", value: "あり" },
      { key: "なし", value: "なし" },
    ],

    /**
     * 引取方法
     * @type {Array}
     */
    HIKITORI_WAYS: [
      { key: "不明", value: "不明" },
      { key: "積載車", value: "積載車" },
      { key: "ウインチ", value: "ウインチ" },
      { key: "レッカー", value: "レッカー" },
      { key: "ユニック", value: "ユニック" },
    ],

    /**
     * メーカー
     * @type {Array}
     */
    CAR_MAKER: [
      { key: "1", value: "国産乗用" },
      { key: "2", value: "大型" },
      { key: "3", value: "輸入車" },
    ],

    /**
     * 車輌ハンドル
     * @type {Array}
     */
    SHARYO_HANDLE: [
      { key: 1, value: "右" },
      { key: 2, value: "左" },
    ],

    /**
     * 再見積理由
     * @type {Array}
     */
    RE_ESTIMATE: [
      { key: 1, value: "注文したが、在庫が無かった" },
      { key: 2, value: "価格が高かった" },
      { key: 3, value: "色違い" },
      { key: 4, value: "品質・状態面" },
      { key: 5, value: "ユーザーによる修理キャンセル→再依頼" },
      { key: 6, value: "発注期限切れ" },
      { key: 7, value: "前回見積り回答ゼロ" },
      { key: 8, value: "追加部品の見積依頼" },
      { key: 99, value: "その他" },
    ],

    /**
     * 現車状態
     * @type {Array}
     */
    GENSHA_STATUS: [
      { key: 1, value: "入力途中" },
      { key: 5, value: "公開" },
      { key: 9, value: "非公開" },
      { key: 99, value: "無効" },
    ],

    /**
     * 走行距離
     * @type {Array}
     */
    SOUKOU_FLG: [
      { key: 1, value: "走行距離" },
      { key: 2, value: "不明" },
      { key: 3, value: "交換" },
      { key: 4, value: "改ざん" },
    ],

    /**
     * 現車用、書類
     * @type {Array}
     */
    GENSHA_SHORUI: [
      { key: 0, value: "不明" },
      { key: 1, value: "書類あり" },
      { key: 9, value: "書類なし" },
    ],

    /**
     * 現車用、エンジン
     * @type {Array}
     */
    GENSHA_ENGINE: [
      { key: "不明", value: "不明" },
      { key: "始動可", value: "始動可" },
      { key: "始動不可", value: "始動不可" },
    ],

    /**
     * 現車用、ミッション
     * @type {Array}
     */
    GENSHA_MISSION: [
      { key: "不明", value: "不明" },
      { key: "利用可", value: "利用可" },
      { key: "利用不可", value: "利用不可" },
    ],
  },

  // システム区分
  KUBUN_PARTS: 1,
  KUBUN_CARS: 2,

  // 質問タイプ
  QA_TYPE: {
    TYPE_Q: 1,
    TYPE_A: 2,
  },

  // 燃料種別
  E_KUBUN: {
    1: "ガソリン",
    2: "軽油",
    3: "ＬＰＧ",
    4: "電気",
    5: "CNG",
    99: "その他",
  },

  /**
   * 見積状態
   * @type {Object}
   */
  MITSUMORI_STATUS: {
    /** 見積回答有 */
    KAITO_ARU: 7,
    /** 見積期限切れ */
    KIGEN_GIRE: 9,
    /** 注文履歴 */
    CHUMON_RIREKI: 10,
  },

  /**
   * 業者フラグ
   * @type {Object}
   */
  GYOSHA_FLG: {
    /** 通常 */
    NORMAL: 0,
    /** 期限切れ */
    KIGEN_GIRE: 1,
    /** 確定済み */
    KAKUTEI: 2,
    MIDOKU: 0, // 未読
    KIDOKU: 1, // 既読
  },

  /**
   * PDF生成関連の定数
   * @type {Object}
   */
  PDF_CONSTANTS: {
    /** Movi企業プレフィックス */
    MOVI_CORP_PREFIX: "24000001",
    /** 大信企業プレフィックス */
    DAISHIN_CORP_PREFIX: "24001321",
    /** 昭和企業ID */
    SHOWA_CORP_ID: "14000001-010",
    /** 消費税率 */
    TAX_RATE: 0.1,
    /** PDFマージン設定 */
    PDF_MARGINS: {
      top: "10mm",
      right: "10mm",
      bottom: "10mm",
      left: "10mm",
    },
  },

  /**
   * 発送可能日マッピング（PDF用）
   * @type {Object}
   */
  HASSO_STATUS_MAP: {
    1: "即日",
    2: "翌日",
    3: "2日後",
    4: "3日後",
    5: "4日後",
    6: "5日後",
    7: "1週間後",
    8: "2週間後",
    9: "3週間後",
    10: "1ヶ月後",
  },

  /**
   * 発送方法マッピング（PDF用）
   * @type {Object}
   */
  HAISO_KUBUN_MAP: {
    1: "宅配便",
    2: "メール便",
    3: "その他",
  },

  /**
   * 在庫回答メール件名（PHP: MAILSUB_ODRD_ZAIKO_KAITO*）
   */
  MAILSUB_ODRD_ZAIKO_KAITO: {
    /** 在庫あり回答：受注確定（メール1-1 YZ宛） */
    "1_1": "受注を確定いたしました",
    /** 在庫あり回答：注文確定（メール1-2 AB宛） */
    "1_2": "注文が確定いたしました",
    /** 在庫なし回答：受注キャンセル（メール2-1 YZ宛） */
    "2_1": "受注をキャンセルいたしました",
    /** 在庫なし回答：部品在庫無しのおしらせ（メール2-2 AB向け） */
    "2_2": "部品在庫無しのおしらせ",
  },

  /**
   * 部品カテゴリ色設定（PDF用）
   * @type {Object}
   */
  PARTS_CATEGORY_COLORS: {
    /** リユース */
    REUSE: { bg: "#CC0000", text: "#FFFFFF" },
    /** リビルト */
    REBUILT: { bg: "#006600", text: "#FFFFFF" },
    /** 純正新品 */
    GENUINE: { bg: "#660066", text: "#FFFFFF" },
    /** 優良新品 */
    QUALITY: { bg: "#0000CC", text: "#FFFFFF" },
    /** デフォルト */
    DEFAULT: { bg: "#CCCCCC", text: "#000000" },
  },
};

export default Consts;
